import { WelcomeExplorerActionId } from 'utils/constants';
import { isDev } from 'utils/env';

const gateway = isDev ? '/api' : process.env.REACT_APP_GATEWAY_URL;

export const practiceUrls = {
  listPractices: {
    queryUrl: 'list-practices',
    apiUrls: (providerId: string) => ({
      list: `${gateway}/provider/${providerId}/practice/list`,
    }),
  },
  listPlatformPractices: {
    queryUrl: 'list-platform-practices',
    apiUrls: (queryParams?: string) => ({
      list: `${gateway}/chief-admin/practice/list${queryParams ? `?${queryParams}` : ''}`,
    }),
  },
  listPracticeProviders: {
    queryUrl: 'list-practice-providers',
    apiUrls: (practiceId: string, queryParams?: string) => ({
      list: `${gateway}/practice/${practiceId}/list-providers${queryParams ? `?${queryParams}` : ''}`,
    }),
  },
};

export const trackingUrls = {
  moodList: {
    queryUrl: 'mood-tracking-list',
    apiUrls: (
      providerId: string,
      patientId: string,
      startDate: string,
      endDate: string,
      mood: string,
    ) => ({
      list: `${gateway}/provider/${providerId}/patient/${patientId}/tracking/moods/${mood}?start-date=${startDate}&end-date=${endDate}`,
    }),
  },
  moodAverage: {
    queryUrl: 'mood-tracking-average',
    apiUrls: (providerId: string, patientId: string, startDate: string, endDate: string) => ({
      list: `${gateway}/provider/${providerId}/patient/${patientId}/mood?start-date=${startDate}&end-date=${endDate}&operation=TRACKING_DATA_OPERATION_AVERAGE`,
    }),
  },
  waypoints: {
    queryUrl: 'waypoint-tracking',
    apiUrls: (providerId: string, patientId: string, startDate: string, endDate: string) => ({
      list: `${gateway}/provider/${providerId}/patient/${patientId}/tracking/waypoints?start-date=${startDate}&end-date=${endDate}`,
    }),
  },
  waypointAverage: {
    queryUrl: 'waypoint-tracking-average',
    apiUrls: (providerId: string, patientId: string, startDate: string, endDate: string) => ({
      list: `${gateway}/provider/${providerId}/patient/${patientId}/waypoint?start-date=${startDate}&end-date=${endDate}&operation=TRACKING_DATA_OPERATION_AVERAGE`,
    }),
  },
  waypoint: {
    queryUrl: 'waypoint-tracking',
    apiUrls: (
      providerId: string,
      patientId: string,
      waypointId: string,
      startDate?: string,
      endDate?: string,
      duration?: string,
      sourceId?:string,
    ) => ({
      list: `${gateway}/provider/${providerId}/patient/${patientId}/tracking/waypoints/${waypointId}?start-date=${startDate}&end-date=${endDate}&duration=${duration}${sourceId ? `&source-id=${sourceId}` : ''}`,
    }),
  },
  waypointDetails: {
    queryUrl: 'waypoint-details',
    apiUrls: (
      providerId: string,
      patientId: string,
      waypointId: string,
      startDate?: string,
      endDate?: string,
      duration?: string,
      sourceId?: string,
    ) => ({
      list: `${gateway}/provider/${providerId}/patient/${patientId}/tracking/waypoint/details/${waypointId}?start-date=${startDate}&end-date=${endDate}&duration=${duration}&source-id=${sourceId}`,
    }),
  },
  waypointSources: {
    apiUrls: (
      providerId: string,
      patientId: string,
      waypointId:string,
    ) => ({
      list: `${gateway}/provider/${providerId}/patient/${patientId}//tracking/waypoint/sources?waypoint-id=${waypointId}`,
    }),
    queryUrl: 'waypoint-sources',
  },
  mostLoggedMetrics: {
    apiUrls: (
      providerId: string,
      patientId: string,
      startDate: string,
      endDate: string,
      entity: string,
      entityKey = 'entity',
    ) => ({ list: `${gateway}/provider/${providerId}/patient/${patientId}/insights/${entityKey === 'mood' ? 'mood-factors-domains' : 'most-logged-metrics'}?start-date=${startDate}&end-date=${endDate}&${entityKey}=${entity}` }),
    queryUrl: 'most-logged-metrics',
  },
  somaticScore: {
    apiUrls: (
      providerId: string,
      patientId: string,
      startDate: string,
      endDate: string,
      duration?: string,
    ) => ({ list: `${gateway}/provider/${providerId}/patient/${patientId}/somaticScore?start-date=${startDate}&end-date=${endDate}&duration=${duration}` }),
    queryUrl: 'somatic-score',
  },
};

export const gamesUrls = {
  stroopTimeSeries: {
    queryUrl: 'stroop-time-series',
    apiUrls: (
      providerId: string,
      patientId: string,
      startDate: string,
      endDate: string,
      duration?: string,
    ) => ({
      list: `${gateway}/provider/${providerId}/patient/${patientId}/games/2f8cfb5b-7d51-4704-9b32-ac6e6726dd22/timeSeries?start-date=${startDate}&end-date=${endDate}&duration=${duration}`,
    }),
  },
};

export const whoIam = {
  categories: {
    queryUrl: 'who-i-am-categories',
    apiUrl: (providerId: string, patientId: string) => ({
      list: `${gateway}/provider/${providerId}/patient/${patientId}/whoiam/categories`,
    }),
  },
  categoryResults: {
    queryUrl: 'who-i-am-category-result',
    apiUrls: (providerId: string, patientId: string, categoryId: string) => ({
      getCategoryResult: `${gateway}/provider/${providerId}/patient/${patientId}/whoiam/categories/${categoryId}/results?limit=2&page=0`,
    }),
  },
  exportData: {
    queryUrl: 'who-i-am-export-data',
    apiUrl: {
      getData: `${gateway}/platform/whoiam/export`,
    },
  },
};

export const patientExport = {
  queryUrl: 'patient-export-data',
  apiUrl: (providerId: string, patientId: string, asPDF?: boolean) => ({
    getData: `${gateway}/provider/${providerId}/patient/${patientId}/export${asPDF ? '?fileType=pdf' : ''}`,
  }),
};

export const patientUrls = {
  createPatient: {
    queryUrl: 'create-patient',
    apiUrls: (providerId: string) => ({
      create: `${gateway}/provider/${providerId}/patient/create`,
    }),
  },
  invitePatients: {
    queryUrl: 'invite-patients',
    apiUrls: (providerId: string) => ({
      create: `${gateway}/v2/provider/${providerId}/patient/create`,
    }),
  },
  listPatients: {
    queryUrl: 'list-patients',
    apiUrls: (providerId: string, status?: string, searchQuery?: string, sortDirection?: 'asc' | 'desc', sortBy?: string, offset?:number, limit?:number, providerIdFilter?:string) => ({
      list: `${gateway}/v2/provider/${providerId}/patient/list${status ? `?status=${status}` : ''}`,
    }),
  },
  listPatientsNew: {
    queryUrl: 'list-patients',
    apiUrls: (providerId: string, status?: string, searchQuery?: string, sortDirection?: 'asc' | 'desc', sortBy?: string, offset?:number, limit?:number, providerIdFilter?:string) => {
      const queryParams = new URLSearchParams();

      if (status) queryParams.append('status', status);
      if (searchQuery) queryParams.append('name', searchQuery);
      if (sortBy) queryParams.append('sortBy', sortBy ?? '');
      if (sortDirection) queryParams.append('sortDirection', sortDirection);
      if (offset) queryParams.append('offset', offset.toString());
      if (limit) queryParams.append('limit', limit.toString());
      if (providerIdFilter) queryParams.append('providerId', providerIdFilter.toString());


      return {
        list: `${gateway}/v2/provider/${providerId}/patient/v2/list${queryParams.toString() ? `?${queryParams.toString()}` : ''}`,
      };
    },
  },
  patientsCount: {
    queryUrl: 'patients-count',
    apiUrls: (providerId: string, searchQuery?:string, providerIdFilter?:string) => ({

      count: `${gateway}/v2/provider/${providerId}/patient/status/count`,
    }),
  },
  patientsCountNew: {
    queryUrl: 'patients-count',
    apiUrls: (providerId: string, searchQuery?: string, providerIdFilter?: string) => {
      const queryParams = new URLSearchParams();

      if (searchQuery) queryParams.append('name', searchQuery);
      if (providerIdFilter) queryParams.append('providerId', providerIdFilter.toString());
      return { count: `${gateway}/v2/provider/${providerId}/patient/v2/status/count${queryParams.toString() ? `?${queryParams.toString()}` : ''}` };
    },
  },
  resendPatientInvite: {
    queryUrl: 'resend-patient-invite',
    apiUrls: (providerId: string) => ({
      create: `${gateway}/v2/provider/${providerId}/patient/create/retry`,
    }),
  },
  patientDetails: {
    queryUrl: 'patient-details',
    apiUrls: (providerId: string, patientId: string) => ({
      details: `${gateway}/provider/${providerId}/patient/${patientId}`,
    }),
  },
  patientHealthRecordStatus: {
    queryUrl: 'patient-health-record-status',
    apiUrls: (providerId: string, patientId: string) => ({
      status: `${gateway}/provider/${providerId}/patient/${patientId}/health-records/status`,
    }),
  },
};

export const medicationsUrls = {
  listMedications: {
    queryUrl: 'list-medications',
    apiUrls: (providerId: string, patientId: string, refreshId?: string) => ({
      list: `${gateway}/provider/${providerId}/patient/${patientId}/medications/list?${refreshId ? `refresh-id=${refreshId}` : ''}`,
      listCurrent: `${gateway}/provider/${providerId}/patient/${patientId}/medications/list?current=true${refreshId ? `&refresh-id=${refreshId}` : ''}`,
      listAll: `${gateway}/provider/${providerId}/patient/${patientId}/medications/list?grouped-summary=True${refreshId ? `&refresh-id=${refreshId}` : ''}`,
    }),
  },
  refills: {
    queryUrl: 'refills',
    apiUrls: (
      patientId: string,
      medicationMetaId: string,
      providerId: string,
      identifierType: string,
    ) => ({
      list: `${gateway}/provider/${providerId}/patient/${patientId}/medications/${medicationMetaId}/refills?type=${identifierType}`,
    }),
  },
  medicationHistory: {
    queryUrl: 'medication-history',
    apiUrls: (
      providerId: string,
      patientId: string,
    ) => ({
      list: `${gateway}/provider/${providerId}/patient/${patientId}/medications/timelines`,
    }),
  },
  counts: {
    queryUrl: 'medications-count',
    apiUrls: (providerId: string, patientId: string) => ({
      count: `${gateway}/provider/${providerId}/patient/${patientId}/health-records/summary/count`,
    }),
  },
};

export const diagnosisUrls = {
  listDiagnosis: {
    queryUrl: 'list-diagnosis',
    apiUrls: (providerId: string, patientId: string, summary?:string, refreshId?: string) => ({
      list: `${gateway}/provider/${providerId}/patient/${patientId}/diagnosis/list?summary=${summary}${refreshId ? `&refresh-id=${refreshId}` : ''}`,
      listCurrent: `${gateway}/provider/${providerId}/patient/${patientId}/diagnosis/list?current=true${refreshId ? `&refresh-id=${refreshId}` : ''}`,
    }),
  },
  history: {
    queryUrl: 'diagnosis-history',
    apiUrls: (diagnosisId:string, providerId: string, patientId: string) => ({
      list: `${gateway}/provider/${providerId}/patient/${patientId}/diagnosis/${diagnosisId}/history`,
    }),
  },
};

export const familyHistoryUrls = {
  listFamilyHistory: {
    queryUrl: 'list-family-history',
    apiUrls: (providerId: string, patientId: string, refreshId?: string) => ({
      list: `${gateway}/provider/${providerId}/patient/${patientId}/family-history/list?${refreshId ? `refresh-id=${refreshId}` : ''}`,
    }),
  },
};

export const symptomsUrls = {
  listSymptoms: {
    queryUrl: 'list-symptoms',
    apiUrls: (providerId: string, patientId: string) => ({
      list: `${gateway}/provider/${providerId}/patient/${patientId}/symptoms/list`,
      listCurrent: `${gateway}/provider/${providerId}/patient/${patientId}/symptoms/list?current=true`,
    }),
  },
};

export const hospitalizationsUrls = {
  listHospitalizations: {
    queryUrl: 'list-hospitalizations',
    apiUrls: (providerId: string, patientId: string, refreshId?: string) => ({
      list: `${gateway}/provider/${providerId}/patient/${patientId}/hospitalizations/list?${refreshId ? `refresh-id=${refreshId}` : ''}`,
    }),
  },
};

export const labsUrls = {
  listLabs: {
    queryUrl: 'list-labs',
    apiUrls: (providerId: string, patientId: string, refreshId?: string) => ({
      list: `${gateway}/provider/${providerId}/patient/${patientId}/labs/list?${refreshId ? `refresh-id=${refreshId}` : ''}`,
    }),
  },
  labReport: {
    queryUrl: 'labs-report',
    apiUrls: (providerId: string, patientId: string, labId: string) => ({
      report: `${gateway}/provider/${providerId}/patient/${patientId}/labs/${labId}/pdf`,
    }),
  },
};

export const allergiesUrls = {
  listAllergies: {
    queryUrl: 'list-allergies',
    apiUrls: (providerId: string, patientId: string, refreshId?: string) => ({
      list: `${gateway}/provider/${providerId}/patient/${patientId}/allergies/list?${refreshId ? `refresh-id=${refreshId}` : ''}`,
    }),
  },
};

export const patientProviderUrls = {
  listPatientProviders: {
    queryUrl: 'list-patient-providers',
    apiUrls: (providerId: string, patientId: string, refreshId?: string) => ({
      list: `${gateway}/provider/${providerId}/patient/${patientId}/providers/list`,
      listCurrent: `${gateway}/provider/${providerId}/patient/${patientId}/providers/list?current=True`,
      listAll: `${gateway}/provider/${providerId}/patient/${patientId}/providers/list?all-providers=True${refreshId ? `&refresh-id=${refreshId}` : ''}`,
    }),
  },
  listPatientPrimaryCareProviders: {
    queryUrl: 'list-patient-primary-care-providers',
    apiUrls: (providerId: string, patientId: string) => ({
      list: `${gateway}/provider/${providerId}/patient/${patientId}/primary-care-provider`,
      listCurrent: `${gateway}/provider/${providerId}/patient/${patientId}/primary-care-provider?current=true`,
    }),
  },
  listPatientMentalHealthProviders: {
    queryUrl: 'list-patient-mental-health-providers',
    apiUrls: (providerId: string, patientId: string) => ({
      list: `${gateway}/provider/${providerId}/patient/${patientId}/mentalhealth-care-provider`,
      listCurrent: `${gateway}/provider/${providerId}/patient/${patientId}/mentalhealth-care-provider?current=true`,
    }),
  },
};

export const lifestyleUrls = {
  listLifestyle: {
    queryUrl: 'list-lifestyle',
    apiUrls: (providerId: string, patientId: string) => ({
      list: `${gateway}/provider/${providerId}/patient/${patientId}/life-style/list`,
      listCurrent: `${gateway}/provider/${providerId}/patient/${patientId}/life-style/list?current=true`,
    }),
  },
};

export const backgroundsUrls = {
  listBackgrounds: {
    queryUrl: 'list-backgrounds',
    apiUrls: (providerId: string, patientId: string) => ({
      list: `${gateway}/provider/${providerId}/patient/${patientId}/backgrounds/list?current=true`,
    }),
  },
};

export const wayPointsUrls = {
  listWayPoints: {
    queryUrl: 'list-waypoint',
    apiUrls: (providerId: string) => ({
      list: `${gateway}/provider/${providerId}/waypoint/list`,
    }),
  },
  listPatientWaypoints: {
    queryUrl: 'patient-waypoints',
    apiUrls: (patientId: string) => ({
      list: `${gateway}/patient/${patientId}/waypoint`,
    }),
  },
  createWayPoint: {
    queryUrl: 'create-waypoint',
    apiUrls: (providerId: string) => ({
      create: `${gateway}/provider/${providerId}/waypoint/create`,
    }),
  },
};

export const wayPointSuggestionUrls = {
  listWaypointSuggestions: {
    queryUrl: 'waypoint-suggestions',
    apiUrls: (providerId: string, patientId: string) => ({
      list: `${gateway}/provider/${providerId}/patient/${patientId}/waypoint/suggestion`,
    }),
  },
  createWaypointSuggestions: {
    queryUrl: 'create-waypoint-suggestions',
    apiUrls: (providerId: string, patientId: string) => ({
      create: `${gateway}/provider/${providerId}/patient/${patientId}/waypoint/suggestion`,
    }),
  },
  deleteWaypointSuggestions: {
    queryUrl: 'revoke-waypoint-suggestions',
    apiUrls: (providerId: string, patientId: string) => ({
      delete: `${gateway}/provider/${providerId}/patient/${patientId}/waypoint/suggestion`,
    }),
  },
};

export const providerSignUpUrls = {
  createProvider: {
    queryUrl: 'create-provider',
    apiUrls: () => ({
      create: `${gateway}/provider-request/create`,
    }),
  },
  listProviderRequest: {
    queryUrl: 'list-provider-request',
    apiUrls: () => ({
      list: `${gateway}/chief-admin/provider-requests`,
    }),
  },
};

export const providerRequestActionUrls = {
  approveProviderRequest: {
    queryUrl: 'approve-provider-request',
    apiUrls: (id: string) => ({
      approveProviderRequest: `${gateway}/chief-admin/provider-requests/${id}/approve`,
    }),
  },
};

export const providers = {
  practiceProvidersList: {
    queryUrl: 'practice-providers-list',
    apiUrl: (practiceId: string, designations?: string) => `${gateway}/practice/${practiceId}/list-providers?designations=${designations}`,
  },
  providerAssociationList: {
    queryUrl: 'providers-associations-list',
    apiUrl: (providerId: string) => `${gateway}/staff/${providerId}/list-association`,
  },
  metadata: {
    queryUrl: 'user-metadata',
    apiUrl: () => `${gateway}/users/user-metadata`,
  },
  postMetadata: {
    queryUrl: 'user-metadata-create',
    apiUrl: () => `${gateway}/users/user-metadata-create`,
  },
  associateProviders: {
    queryUrl: 'associate-providers',
    apiUrl: (associate: boolean) => `${gateway}/chief-admin/${associate ? 'create-association' : 'delete-association'}`,
  },
  resendActivationEmail: {
    queryUrl: 'resend-activation-email',
    apiUrl: (providerId: string) => `${gateway}/chief-admin/provider/${providerId}/resend-activation-email`,
  },
  updateEmailAndResendActionEmail: {
    queryUrl: 'update-email-and-resend-activation-email',
    apiUrl: (providerId: string) => `${gateway}/chief-admin/provider/${providerId}/update-provider-email`,
  },
  providerDetails: {
    queryUrl: 'provider-details',
    apiUrl: (providerId: string) => `${gateway}/provider/${providerId}/details`,
  },
};

export const providerConsentUrls = {
  consent: {
    queryUrl: 'provider-consent',
    apiUrl: (providerId: string) => `${gateway}/provider/${providerId}/consent`,
  },
  giveConsent: {
    queryUrl: 'give-provider-consent',
    apiUrl: (providerId: string) => `${gateway}/provider/${providerId}/consent`,
  },
};

export const rewardsUrls = {
  rewardPoints: {
    queryUrl: 'get-provider-reward-points',
    apiUrl: (providerId: string) => `${gateway}/provider/${providerId}/strapi/clinicians?filters[providerId][$eq]=${providerId}`,
  },
  rewards: {
    queryUrl: 'get-all-rewards',
    apiUrl: (providerId: string) => `${gateway}/provider/${providerId}/strapi/rewards?sort[0]=PointsRequired:asc`,
  },
  explorerActions: {
    queryUrl: 'get-all-explorer-actions',
    apiUrl: (providerId: string) => `${gateway}/provider/${providerId}/strapi/explorer-actions?filters[identifier][$ne]=${WelcomeExplorerActionId}&sort[0]=Points:asc`,
  },
  transactions: {
    queryUrl: 'get-provider-transactions',
    apiUrl: (providerId: string) => `${gateway}/provider/${providerId}/strapi/transactions?populate=*&sort[0]=date:desc&filters[clinician][providerId][$eq]=${providerId}`,
  },
  sendReferral: {
    queryUrl: 'send-referral-eamil',
    apiUrl: (providerId: string) => `${gateway}/provider/${providerId}/referral/send`,
  },
};

export const providerPracticesUrls = {
  listProviderPractices: {
    queryUrl: 'list-provider-practices',
    apiUrls: () => ({
      list: `${gateway}/provider/list/provider-practices`,
    }),
  },
};

export const scalesManifestUrls = {
  listScalesManifest: {
    queryUrl: 'list-scales-manifest',
    apiUrl: (
      categoryId: string,
      providerId: string,
      patientId: string,
      startDate: string,
      endData: string,
      attemptId: string,
      page: number,
      size: number,
      billingAdminId: string,
      showLatestAttempts: boolean,
    ) => {
      const apiUrlParams = {
        category_id: categoryId,
        'provider-id': providerId,
        'patient-id': patientId,
        'start-date': startDate,
        'end-date': endData,
        attemptId,
        offset: page,
        limit: size,
        'latest-report': showLatestAttempts,
      };

      const apiUrlParamsString = Object.entries(apiUrlParams)
        .filter(([_, value]) => !!value || value === 0)
        .map(([key, value]) => `${key}=${value}`)
        .join('&');

      const listUrl = `${gateway}/provider/${billingAdminId}/whoiam/categories/results/details?${apiUrlParamsString}`; return ({
        list: listUrl,
      });
    },
  },
  attemptDetails: {
    queryUrl: 'attempt-details',
    apiUrl: (categoryId: string, providerId: string, attemptId?: string) => ({
      details: `${gateway}/provider/${providerId}/whoiam/categories/${categoryId}/results/attempt/details${attemptId ? `?attempt_id=${attemptId}` : ''}`,
    }),
  },
  addNotes: {
    queryUrl: 'add-notes',
    apiUrl: (categoryId: string, providerId: string, attemptId: string) => ({
      add: `${gateway}/provider/${providerId}/whoiam/categories/${categoryId}/attempt/${attemptId}`,
    }),
  },
  sendScaleSuggestions: {
    queryUrl: 'send-scale-suggestions',
    apiUrl: (patientId: string, providerId: string) => ({
      send: `${gateway}/provider/${providerId}/whoiam/categories/suggestions?patient-id=${patientId}`,
    }),
  },
};

export const chiefAdminUrls = {
  listAuditLogs: {
    queryUrl: 'list-audit-logs',
    apiUrls: (queryParams?: string) => ({
      list: `${gateway}/chief-admin/audit-trail${queryParams ? `${queryParams}` : ''}`,
    }),
  },
  listCategories: {
    queryUrl: 'list-categories',
    apiUrls: (categoryType: string) => ({
      list: `${gateway}/platform/categories?category_type=${categoryType}`,
    }),
  },
  listPracticeCategories: {
    queryUrl: 'list-practice-categories',
    apiUrls: (practiceId: string, categoryType: string) => ({
      list: `${gateway}/chief-admin/practice/${practiceId}/bundles?category_type=${categoryType}`,
    }),
  },
  updatePracticeBundle: {
    queryUrl: 'update-practice-bundle',
    apiUrls: (practiceId: string, bundleId?: string) => ({
      update: `${gateway}/chief-admin/practice/${practiceId}/bundles${bundleId ? `/${bundleId}` : ''}`,
    }),
  },
  deletePracticeBundle: {
    queryUrl: 'delete-practice-bundle',
    apiUrls: (practiceId: string, bundleId: string) => ({
      delete: `${gateway}/chief-admin/practice/${practiceId}/bundles/${bundleId}`,
    }),
  },
  deletePatient: {
    queryUrl: 'delete-patient',
    apiUrls: () => ({
      delete: `${gateway}/chief-admin/delete/patient`,
    }),
  },
};

export const sessionRecordingUrls = {
  generateNotes: {
    queryUrl: 'generate-notes',
    apiUrl: (providerId: string, patientId: string) => `${gateway}/provider/${providerId}/patient/${patientId}/notetaker/generate-note`,
  },
  getDestinationUrls: {
    queryUrl: 'get-destination-urls',
    apiUrl: (providerId: string, patientId: string, sessionId: string) => `${gateway}/provider/${providerId}/patient/${patientId}/notetaker/destination${sessionId ? `?sessionId=${sessionId}` : ''}`,
  },
  sendTranscriptFeedback: {
    queryUrl: 'send-transcript-feedback',
    apiUrl: (providerId: string, patientId: string, sessionId: string) => `${gateway}/provider/${providerId}/patient/${patientId}/notetaker/sessions/${sessionId}/feedback`,
  },
  editTranscript: {
    queryUrl: 'edit-transcript',
    apiUrl: (providerId: string, patientId: string, sessionId: string) => `${gateway}/provider/${providerId}/patient/${patientId}/notetaker/sessions/${sessionId}/edit`,
  },
  getSessions: {
    queryUrl: 'get-session-details',
    apiUrl: (providerId: string, patientId: string, queryParams: string) => `${gateway}/provider/${providerId}/patient/${patientId}/notetaker/sessions${queryParams}`,
  },
  getMetadata: {
    queryUrl: 'get-metadata',
    apiUrl: (providerId: string, patientId: string) => `${gateway}/provider/${providerId}/patient/${patientId}/notetaker/metadata`,
  },
  getSessionAuthToken: {
    queryUrl: 'get-session-auth-token',
    apiUrl: (providerId: string, patientId: string, sessionId?: string) => `${gateway}/provider/${providerId}/patient/${patientId}/notetaker/transcription/details/session/${sessionId}`,
  },
  getPDFUrl: {
    queryUrl: 'get-pdf-url',
    apiUrl: (providerId: string, patientId: string, sessionId: string) => `${gateway}/provider/${providerId}/patient/${patientId}/notetaker/sessions/${sessionId}/download`,
  },
};

export const healthRecordRefreshUrls = {
  createHrRefresh: {
    queryUrl: 'create-hr-refresh',
    apiUrls: (
      providerId: string,
      patientId: string,
    ) => ({
      create: `${gateway}/provider/${providerId}/patient/${patientId}/hr-refresh`,
    }),
  },
  hrRefreshHistory: {
    queryUrl: 'hr-refresh-history',
    apiUrls: (
      providerId: string,
      patientId: string,
    ) => ({
      list: `${gateway}/provider/${providerId}/patient/${patientId}/hr-refresh/history`,
    }),
  },
  notifications: {
    queryUrl: 'notifications',
    apiUrls: (providerId: string, queryParams?: string) => ({
      list: `${gateway}/provider/${providerId}/notifications${queryParams ? `?${queryParams}` : ''}`,
    }),
  },
  unreadNotifications: {
    queryUrl: 'unread-notifications',
    apiUrls: (providerId: string) => ({
      count: `${gateway}/provider/${providerId}/notifications/count`,
    }),
  },
  markNotificationStatus: {
    queryUrl: 'mark-notification-status-as-read',
    apiUrls: (providerId: string, notificationId: string) => ({
      update: `${gateway}/provider/${providerId}/notifications/${notificationId}`,
    }),
  },
  markAllNotificationsRead: {
    queryUrl: 'mark-all-notifications-as-read',
    apiUrls: (providerId: string) => ({
      update: `${gateway}/provider/${providerId}/notifications/mark-read/all`,
    }),
  },
};

export const bulkUploadUrls = {
  patientBulkUpload: {
    queryUrl: 'patient-bulk-upload',
    apiUrl: (providerId: string) => `${gateway}/v2/provider/${providerId}/patient/bulk-upload`,
  },
  listBulkUploads: {
    queryUrl: 'patient-bulk-upload-list',
    apiUrl: (providerId: string) => `${gateway}/v2/provider/${providerId}/patient/bulk-upload`,
  },
  downloadBulkUpload: {
    queryUrl: 'patient-bulk-upload-download',
    apiUrl: (providerId: string) => `${gateway}/v2/provider/${providerId}/patient/bulk-upload/download`,
  },
};

export const freeTrialProviderRequestUrls = {
  createFreeTrialProvider: {
    queryUrl: 'free-trial-provider-request-create',
    apiUrl: () => `${gateway}/free-trial/provider-request/create`,
  },
  sendOtp: {
    queryUrl: 'free-trial-provider-request-send-otp',
    apiUrl: () => `${gateway}/free-trial/provider-request/send-otp`,
  },
  verifyOtp: {
    queryUrl: 'free-trial-provider-request-verify-otp',
    apiUrl: () => `${gateway}/free-trial/provider-request/verify-otp`,
  },
  changePassword: {
    queryUrl: 'free-trial-provider-request-change-password',
    apiUrl: () => `${gateway}/free-trial/provider-request/change-password`,
  },
  practicesDropdown: {
    queryUrl: 'free-trial-provider-request-practices-dropdown',
    apiUrl: (queryParams?: string) => ({
      list: `${gateway}/free-trial/provider-request/practices-dropdown${queryParams ? `?Query=${queryParams}` : ''}`,
    }),
  },
  associatePractice: {
    queryUrl: 'free-trial-provider-request-associate-practice',
    apiUrl: () => `${gateway}/free-trial/provider-request/associate-practice`,
  },
  inviteProviders: {
    queryUrl: 'free-trial-provider-request-invite-providers',
    apiUrl: () => `${gateway}/free-trial/provider-request/invite-providers`,
  },
  checkoutSession: {
    queryUrl: 'free-trial-provider-request-checkout-session',
    apiUrl: () => `${gateway}/free-trial/provider-request/checkout-session`,
  },
  onboardingStatus: {
    queryUrl: 'free-trial-provider-request-onboarding-status',
    apiUrl: () => `${gateway}/free-trial/provider-request/onboarding-status`,
  },
  updateGoal: {
    queryUrl: 'free-trial-provider-request-update-goal',
    apiUrl: (providerId: string) => `${gateway}/provider/${providerId}/update-goal`,
  },
  patientStats: {
    queryUrl: 'free-trial-provider-request-patient-stats',
    apiUrl: (providerId: string) => `${gateway}/v2/provider/${providerId}/patient/stats`,
  },
};

export const shareLightUrls = {
  submit: {
    apiUrl: () => ({
      submit: `${gateway}/share-light/submit`,
    }),
  },
  publicIds: {
    apiUrl: () => ({
      list: `${gateway}/share-light/public-ids`,
    }),
  },
  submission: {
    apiUrl: (id: string) => ({
      details: `${gateway}/share-light/submission/${id}`,
    }),
  },
  pending: {
    apiUrl: () => ({
      list: `${gateway}/chief-admin/share-light/pending`,
    }),
  },
  approve: {
    apiUrl: () => ({
      approve: `${gateway}/chief-admin/share-light/approve`,
    }),
  },
  like: {
    apiUrl: () => ({
      updateLikes: `${gateway}/share-light/update-likes`,
    }),
  },
  csrfToken: {
    apiUrl: () => ({
      get: `${gateway}/share-light/csrf-token`,
    }),
  },
  share: {
    apiUrl: () => ({
      share: `${gateway}/share-light/share`,
    }),
  },
};

